import { Rate, Spin, Tag } from 'antd';
import React, { useState } from 'react';
import './GoogleReviews.css';
import '../../pages.scss';
import { useFetch } from '../../../../hooks/useFetch/useFetch';
import { useAuth } from '../../../../store/AuthContext/AuthContext';
import TableLZM from '../../../components/design/table/TableLZM';
import { CSVDownload } from '../../../../common/utils/CSVDownload';

import { useTranslation } from 'react-i18next';
import { SentimentBar } from './sentiment/SentimentBar';
import { ShopOutlined, SearchOutlined, StarOutlined } from '@ant-design/icons';
import { LocalLabelsModal } from './sentiment/LocalLabelsModal';
import { EmptyStateBox } from '../../../components/design/EmptyStateBox/EmptyStateBox';
import { LABEL_COLOR, SENTIMENT_ICONS } from './Reviews/Label';
import { TableFilter } from '../../../../common/utils/FilterUtils';

const getAverageSentiment = (categories: any[] = []) => {
	const totals = categories.reduce(
		(acc, cat) => ({
			positive: acc.positive + (cat.numberOfPositiveLabels || 0),
			neutral: acc.neutral + (cat.numberOfNeutralLabels || 0),
			negative: acc.negative + (cat.numberOfNegativeLabels || 0),
		}),
		{ positive: 0, neutral: 0, negative: 0 }
	);

	const total = totals.positive + totals.neutral + totals.negative;
	if (!total) {
		return null;
	}

	const positive = Math.round((100 * totals.positive) / total);
	const neutral = Math.round((100 * totals.neutral) / total);
	const negative = Math.max(0, 100 - positive - neutral);

	// Ranking: positivas 100%, neutras 50%, negativas 0%
	const ranking = Math.round(((totals.positive + totals.neutral * 0.5) / total) * 100);

	let dominant: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' = 'NEUTRAL';
	if (totals.positive >= totals.neutral && totals.positive >= totals.negative) {
		dominant = 'POSITIVE';
	} else if (totals.negative >= totals.neutral && totals.negative >= totals.positive) {
		dominant = 'NEGATIVE';
	}

	const dominantLabel = {
		POSITIVE: 'Positivo',
		NEUTRAL: 'Neutro',
		NEGATIVE: 'Negativo',
	}[dominant];

	return {
		positive,
		neutral,
		negative,
		positiveCount: totals.positive,
		neutralCount: totals.neutral,
		negativeCount: totals.negative,
		total,
		dominant,
		dominantLabel,
		ranking,
	};
};

/** Escala continuo rojo (0%) → verde (100%) */
const getRankingColor = (score: number) => {
	const clamped = Math.max(0, Math.min(100, score));
	const hue = (clamped / 100) * 120; // 0=rojo, 60=amarillo, 120=verde
	return `hsl(${hue}, 85%, 34%)`;
};

export const CommentsByLocation = (props: {filterCategory: string, filterSentiment: string, filterLabeled: string, filterPeriod: string, categories: any}) => {

	const [updateData, setUpdateData] = useState(false);
    const [labelsModal, setLabelsModal] = useState<any>(null);
	const [reviews, setReviews] = useState([]);    
	const { projectGroup } = useAuth();
        
    const { t } = useTranslation();

	const { isLoading } = useFetch(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/${projectGroup.group}/comments/byLocation?${props.filterCategory}${props.filterSentiment}${props.filterLabeled}${props.filterPeriod}`
	  	, {},[updateData]
		,(data) => {      			
			setReviews(data.locationWithSentimentInfo);	            
		}
    );

    const columns: any = [       
        {
            title: 'Local',
            key: 'locality',
            width: '280px',
            filterDropdown: TableFilter,
            filterIcon: () => <SearchOutlined />,
            onFilter: (value: any, record: any) => {
                const query = value?.toLowerCase();
                return record.title?.toLowerCase().includes(query)
                    || record.addressLine?.toLowerCase().includes(query)
                    || record.locality?.toLowerCase().includes(query)
                    || record.administrativeArea?.toLowerCase().includes(query);
            },
            render: (_: string, record: any) => (
                <div style={{
                    background: 'transparent',
                    borderRadius: 10,
                    maxWidth: 280,
                    border: '1px solid rgba(148, 163, 184, 0.28)',
                    padding: '10px 12px',
                }}>
                    <div style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#0f172a',
                        lineHeight: 1.3,
                        marginBottom: 4,
                    }}>
                        {record.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.35 }}>
                        {record.addressLine
                            ? `${record.addressLine}, ${record.locality}, ${record.administrativeArea}`
                            : 'Sin ubicación'}
                    </div>
                </div>
            )
        },
        {
            title: 'Ranking de sentimiento',
            key: 'ranking',
            width: 150,
            align: 'left',
            sorter: (a: any, b: any) => {
                const scoreA = getAverageSentiment(a.categories)?.ranking ?? -1;
                const scoreB = getAverageSentiment(b.categories)?.ranking ?? -1;
                return scoreA - scoreB;
            },
            render: (_: string, record: any) => {
                const avg = getAverageSentiment(record.categories);
                if (!avg) {
                    return <i style={{ color: 'lightgrey' }}>—</i>;
                }

                const color = getRankingColor(avg.ranking);

                return (
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: '3px 8px',
                        borderRadius: 6,
                        background: `color-mix(in srgb, ${color} 10%, white)`,
                        border: `1px solid color-mix(in srgb, ${color} 45%, white)`,
                    }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: 14,
                            lineHeight: 1,
                            color,
                        }}>
                            {React.cloneElement(SENTIMENT_ICONS[avg.dominant], {
                                style: { padding: 0, color, fontSize: 14 },
                            })}
                        </span>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                        }}>
                            {avg.dominantLabel}
                        </span>
                        <span style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color,
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            marginLeft: 2,
                        }}>
                            {avg.ranking}%
                        </span>
                    </div>
                );
            }
        },
        {
            title: 'Nº de Etiquetas',
            key: 'avgSentiment',
            width: 260,
            align: 'left',
            render: (_: string, record: any) => {
                const avg = getAverageSentiment(record.categories);
                if (!avg) {
                    return <i style={{ color: 'lightgrey' }}>Sin etiquetas</i>;
                }

                const opiniones = record.totalUniqueReviews || 0;
                const nota = record.avgStarRating != null
                    ? Math.round(10 * record.avgStarRating) / 10
                    : null;

                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        minWidth: 0,
                        width: '100%',
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            flexShrink: 0,
                            fontSize: 11,
                            lineHeight: 1.2,
                        }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <span style={{ display: 'inline-flex', fontSize: 13, color: LABEL_COLOR.POSITIVE }}>
                                    {React.cloneElement(SENTIMENT_ICONS.POSITIVE, {
                                        style: { padding: 0, color: LABEL_COLOR.POSITIVE, fontSize: 13 },
                                    })}
                                </span>
                                <b style={{ color: LABEL_COLOR.POSITIVE, fontSize: 12 }}>{avg.positiveCount}</b>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>({avg.positive}%)</span>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <span style={{ display: 'inline-flex', fontSize: 13, color: LABEL_COLOR.NEUTRAL }}>
                                    {React.cloneElement(SENTIMENT_ICONS.NEUTRAL, {
                                        style: { padding: 0, color: LABEL_COLOR.NEUTRAL, fontSize: 13 },
                                    })}
                                </span>
                                <b style={{ color: LABEL_COLOR.NEUTRAL, fontSize: 12 }}>{avg.neutralCount}</b>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>({avg.neutral}%)</span>
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <span style={{ display: 'inline-flex', fontSize: 13, color: LABEL_COLOR.NEGATIVE }}>
                                    {React.cloneElement(SENTIMENT_ICONS.NEGATIVE, {
                                        style: { padding: 0, color: LABEL_COLOR.NEGATIVE, fontSize: 13 },
                                    })}
                                </span>
                                <b style={{ color: LABEL_COLOR.NEGATIVE, fontSize: 12 }}>{avg.negativeCount}</b>
                                <span style={{ color: '#64748b', fontWeight: 500 }}>({avg.negative}%)</span>
                            </span>
                        </div>
                        <small style={{ color: '#94a3b8', lineHeight: 1.3, width: '100%' }}>
                            {avg.total} etiqueta{avg.total === 1 ? '' : 's'} de {opiniones} opini{opiniones === 1 ? 'ón' : 'ones'}
                            {nota != null && (
                                <> de <StarOutlined style={{ fontSize: 11, marginRight: 2 }} />{nota.toFixed(1)}</>
                            )}
                        </small>
                    </div>
                );
            }
        },
        {
            title: 'Categorías',
            key: 'categories',
            width: 420,
            align: 'left',
            render: (text: string, record: any) => {
                const categories = record.categories || [];
                if (!categories.length) {
                    return <i style={{ color: 'lightgrey' }}>Sin categorías</i>;
                }

                // Obtenemos el máximo de etiquetas para normalizar el ancho
                const maxLabels = Math.max(...categories.map((c: any) => 
                    c.numberOfPositiveLabels + c.numberOfNeutralLabels + c.numberOfNegativeLabels
                ), 1);
        
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 8px' }}>
                        {
                            categories.map((catInfo: any) => {
                                const totalLabels = catInfo.numberOfPositiveLabels + catInfo.numberOfNeutralLabels + catInfo.numberOfNegativeLabels;                                                                
                                const calculatedWidth =  Math.max((totalLabels / maxLabels) * 200, 28);
        
                                return (
                                    <div 
                                        key={catInfo.category}
                                        style={{
                                            display: 'grid', 
                                            gridTemplateColumns: '170px 1fr auto', 
                                            columnGap: '12px',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                            <span style={{
                                                color: '#94a3b8',
                                                fontSize: 12,
                                                lineHeight: 1,
                                                flexShrink: 0,
                                            }}>
                                                •
                                            </span>
                                            <span style={{
                                                fontWeight: 600,
                                                fontSize: 12,
                                                color: '#334155',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                                title={catInfo.category}
                                            >
                                                {catInfo.category}
                                            </span>
                                            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                · {totalLabels} etiqueta{totalLabels === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                        
                                        <SentimentBar 
                                            width={calculatedWidth}
                                            height={5.6}
                                            positive={catInfo.percentPositives} 
                                            neutral={catInfo.percentNeutral} 
                                            negative={catInfo.percentNegatives} 
                                        />
                                        <a
                                            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                                            onClick={() => {
                                                setLabelsModal({
                                                    locationId: record.locationId,
                                                    location: record,
                                                    category: catInfo,
                                                });
                                            }}
                                        >
                                            Ver Etiquetas
                                        </a>
                                    </div>                               
                                );
                            })
                        }                
                    </div>
                );
            }
        },
    ]
    
    const transformarDatos = (reviews: any): any[] => {
		        
		return reviews.map((r : any) => ({
			'Nombre del local': r.title,
			'Localidad': r.locality,
			'Áream Administrativa': r.administrativeArea,
			'Dirección': r.addressLine,
            'Puntuación': Math.round(10 * r.avgStarRating) / 10,
            'Reseñas con etiquetas': r.totalUniqueReviews,
			'Sentimiento Positivo': r.categories?.map((l: any) => {return l["numberOfPositiveLabels"] > 0 ? (l["category"] + " (x" + l["numberOfPositiveLabels"]) + ")": null}).filter((val: any) => val !== null).join(", "),			
            'Sentimiento Neutro': r.categories?.map((l: any) => {return l["numberOfNeutralLabels"] > 0 ? (l["category"] + " (x" + l["numberOfNeutralLabels"]) + ")" : null}).filter((val: any) => val !== null).join(", "),			
            'Sentimiento Negativo': r.categories?.map((l: any) => {return l["numberOfNegativeLabels"] > 0 ? (l["category"] + " (x" + l["numberOfNegativeLabels"]) + ")" : null}).filter((val: any) => val !== null).join(", "),			            
		}));
	  };

	return (
		<div>				
            {/*<div style={{display:'flex', justifyContent:'right'}}>
                <h3 style={{color:'slategray'}}>Categorías</h3>                
            </div>
            <Column style={{padding:'50px'}} data={chartPoints} {...config} height={200}/>   */}

            <Spin spinning={isLoading}>
                {!reviews?.length && !isLoading ? (
                    <div style={{ margin: '12px 0' }}>
                        <EmptyStateBox
                            icon={<ShopOutlined />}
                            title="No hay locales"
                            description="No hay datos de sentimiento por local para los filtros seleccionados."
                        />
                    </div>
                ) : (
                    <TableLZM size='small'                
                        pagination={false} 
                        loading={isLoading}                
                        rowKey="name"
                        columns={columns} 
                        dataSource={reviews}                    
                        />
                )}
            </Spin>
                {!!reviews?.length && <CSVDownload dataSource={transformarDatos(reviews)} title="Descargar CSV" fileName={`Categorías por local.csv`}/>}
                {labelsModal && (
                    <LocalLabelsModal
                        locationId={labelsModal.locationId}
                        location={labelsModal.location}
                        category={labelsModal.category}
                        filterCategory={`&filterCategory=${labelsModal.category.category}`}
                        filterSentiment={props.filterSentiment}
                        filterLabeled={props.filterLabeled}
                        filterPeriod={props.filterPeriod}
                        onFinish={() => setLabelsModal(null)}
                    />
                )}
		</div>
	)
}